import get from 'lodash.get'
import PropTypes from 'prop-types'
import React, { createContext, useContext, useMemo, useState } from 'react'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import { CommonVisualBlocksProps } from './propTypes'
import { processDataRecursively, resetIdCounter } from './utils'

const VisualBlocksContext = createContext()

export function useVisualBlocksContext () {
  const context = useContext(VisualBlocksContext)
  if (!context) {
    throw new Error(
      'useVisualBlocksContext must be used within a VisualBlocksProvider'
    )
  }
  return context
}

export function VisualBlocksProvider ({
  children,
  pipeline,
  variables,
  dataAccessor = 'data',
  nameAccessor = 'name',
  valueAccessor = 'value',
  colorAccessor = 'color',
  detailsAccessor = 'data',
  orderAccessor = 'value',
  orderDirection = 'desc',
  drilldownKeyAccessor = 'orionEntityId',
  ddFilterKey = 'orionEntityIds',
  separatedGroups = [{ accessor: 'name', op: 'eq', value: 'Liabilities' }],
  hideZeroValueAccounts = false,
  listLayout = 'auto',
  colors,
  formats = {
    blocks: {
      0: '(0.0a)',
      1: '(0.0A)'
    },
    tooltips: '0.0a',
    labels: '0.0a',
    hoverDetails: '0.0a',
    listItems: '$0,000.00'
  },
  classNames = {
    container: '',
    blocks: '',
    labels: '',
    listItems: '',
    listItemList: '',
    listItemContainer: '',
    avatar: '',
    treeBranch: '',
    emptyState: ''
  },
  styles = {
    container: {},
    blocks: {},
    rows: {},
    columns: {},
    labels: {},
    listItems: {},
    listItemList: {},
    listItemContainer: {},
    avatar: {},
    treeBranch: {},
    emptyState: {}
  },
  enableManualAccountManagement = true,
  maxBlockDepth = 1,
  maxListDepth = 2,
  includeAvatar = true,
  debug = false,
  ddPipeline,
  ddVariables,
  includeSubtotals = false
}) {
  const { data, isLoading } = usePipelineDataContextWithVariables(
    pipeline,
    variables
  )

  const processedData = useMemo(() => {
    if (!data || isLoading) return []

    // Reset ID counter for consistent ID generation
    resetIdCounter()

    const processed = processDataRecursively(data, {
      dataAccessor,
      nameAccessor,
      valueAccessor,
      colorAccessor,
      detailsAccessor,
      orderAccessor,
      orderDirection,
      separatedGroups,
      hideZeroValueAccounts,
      listLayout,
      colors
    })

    return processed.sort((a, b) => {
      const aValue = a.isSeparated
        ? -Infinity
        : get(a, orderAccessor) ?? -Infinity
      const bValue = b.isSeparated
        ? -Infinity
        : get(b, orderAccessor) ?? -Infinity

      return orderDirection === 'asc' ? aValue - bValue : bValue - aValue
    })
  }, [
    data,
    isLoading,
    dataAccessor,
    nameAccessor,
    valueAccessor,
    colorAccessor,
    detailsAccessor,
    orderAccessor,
    orderDirection,
    separatedGroups,
    hideZeroValueAccounts,
    listLayout,
    colors
  ])

  const [ddVariablesContext, setDdVariablesContext] = useState(ddVariables)
  React.useEffect(() => {
    setDdVariablesContext(ddVariables)
  }, [ddVariables])
  const [ddItem, setDdItem] = useState(null)
  const [filterTable, setFilterTable] = useState(null)
  const contextValue = useMemo(
    () => ({
      data: processedData,
      isLoading,
      formats,
      classNames,
      styles,
      enableManualAccountManagement,
      maxBlockDepth,
      maxListDepth,
      includeAvatar,
      debug,
      drilldownKeyAccessor,
      ddFilterKey,
      hasDrilldown: !!ddPipeline?.length,
      ddItem,
      setDdItem,
      ddPipeline,
      ddVariablesContext,
      setDdVariablesContext,
      filterTable,
      setFilterTable,
      includeSubtotals,
      listLayout
    }),
    [
      processedData,
      isLoading,
      formats,
      classNames,
      styles,
      enableManualAccountManagement,
      maxBlockDepth,
      maxListDepth,
      includeAvatar,
      debug,
      ddItem,
      ddVariablesContext,
      setDdVariablesContext,
      filterTable,
      ddPipeline,
      drilldownKeyAccessor,
      ddFilterKey,
      includeSubtotals,
      listLayout
    ]
  )

  return (
    <VisualBlocksContext.Provider value={contextValue}>
      {children}
      {debug && (
        <pre
          style={{
            position: 'fixed',
            bottom: 0,
            overflowY: 'scroll',
            left: 0,
            right: 0,
            height: '300px',
            border: '1px solid var(--color-neutral-100)'
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </VisualBlocksContext.Provider>
  )
}

VisualBlocksProvider.propTypes = {
  children: PropTypes.node.isRequired,
  ...CommonVisualBlocksProps,
  maxBlockDepth: PropTypes.number,
  maxListDepth: PropTypes.number,
  includeAvatar: PropTypes.bool,
  debug: PropTypes.bool,
  includeSubtotals: PropTypes.bool
}
